import type { ReactElement } from 'react';
import type { StoryVersion, AgeRange } from '@/types/story';
import type { LucideIcon } from 'lucide-react';

import { useState, useEffect, useCallback, useMemo, lazy, Suspense } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import PageLayout from '@/components/PageLayout';
import VersionBadge from '@/components/VersionBadge';
import PublicSeriesCard from '@/components/PublicSeriesCard';
import ClickableAuthor from '@/components/ClickableAuthor';
import { STORY_CATEGORIES } from '@/constants/categories';
import {
  Sparkles, Heart, Eye, BookOpen, ChevronDown,
  Lock, Star, Library, Loader2, Film, List, BookMarked, Users, Crown, User as UserIcon
} from 'lucide-react';
import { getBookTypeLabel } from '@/lib/utils';
import { useInfiniteScroll } from '@/hooks/useInfiniteScroll';
import type { BookshelfStory, CategoryPaginationState } from '@/components/MagicBookshelf';

// Lazy load the bookshelf component
const MagicBookshelf = lazy(() => import('@/components/MagicBookshelf'));

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

interface ExploreStory {
  id: string;
  title: string;
  thumbnail?: string;
  age_range: string;
  category?: string;
  version?: StoryVersion;
  is_pro_story?: boolean;
  is_public?: boolean;
  is_favorited?: boolean;
  author_name?: string;
  author_picture?: string;
  user_id: string;
  pages?: Array<{ page_number: number }>;
  favorites_count?: number;
  views_count?: number;
  created_at: string;
  series_title?: string;
  series_is_public?: boolean;
  has_animations?: boolean;
}

interface ExploreSeries {
  id: string;
  title: string;
  description?: string;
  thumbnail?: string;
  age_range: string;
  book_count: number;
  user_name: string;
  user_picture?: string;
  status?: string;
}

interface SeriesResponse {
  series: ExploreSeries[];
}

interface ExploreCreator {
  user_id: string;
  name: string;
  handle?: string;
  picture?: string;
  bio?: string;
  followers_count: number;
  story_count: number;
  is_premium: boolean;
  is_following: boolean;
}

interface CategoryOption {
  value: string;
  label: string;
  icon?: LucideIcon;
}

interface FilterOption {
  value: string;
  label: string;
}

type TabType = 'stories' | 'series' | 'creators';
type SortType = 'trending' | 'new' | 'popular';
type ProFilterType = 'all' | 'pro' | 'standard';
type AnimatedFilterType = 'all' | 'animated' | 'static';
type ViewMode = 'list' | 'bookshelf';

// Helper to get full image URL (handles both local /static/ paths and external URLs)
const getImageUrl = (url: string | undefined | null): string | null => {
  if (!url) return null;
  if (url.startsWith('/static/')) {
    return `${BACKEND_URL}${url}`;
  }
  return url;
};

interface StoryListItemProps {
  story: ExploreStory;
}

const ExplorePage = (): ReactElement => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const initialTab: TabType = tabParam === 'series' ? 'series' : tabParam === 'creators' ? 'creators' : 'stories';
  const viewParam = searchParams.get('view');
  // Default to list view
  const initialView: ViewMode = viewParam === 'bookshelf' ? 'bookshelf' : 'list';
  const [activeTab, setActiveTab] = useState<TabType>(initialTab);
  const [viewMode, setViewMode] = useState<ViewMode>(initialView);
  const [stories, setStories] = useState<ExploreStory[]>([]);
  const [series, setSeries] = useState<ExploreSeries[]>([]);
  const [creators, setCreators] = useState<ExploreCreator[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [loadingMore, setLoadingMore] = useState<boolean>(false);
  const [category, setCategory] = useState<string>('all');
  const [sort, setSort] = useState<SortType>('new');
  const [proFilter, setProFilter] = useState<ProFilterType>('all');
  const [animatedFilter, setAnimatedFilter] = useState<AnimatedFilterType>('all');
  const [sortDropdownOpen, setSortDropdownOpen] = useState<boolean>(false);
  // Series-specific filters
  const [ageRangeFilter, setAgeRangeFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  // Pagination state
  const [hasMoreStories, setHasMoreStories] = useState<boolean>(true);
  const [hasMoreSeries, setHasMoreSeries] = useState<boolean>(true);
  const [hasMoreCreators, setHasMoreCreators] = useState<boolean>(true);
  // Per-category pagination for bookshelf horizontal scroll
  const [categoryPagination, setCategoryPagination] = useState<CategoryPaginationState>({});
  const [categoryOffsets, setCategoryOffsets] = useState<Record<string, number>>({});
  // Track if any category is loading to prevent vertical scroll conflicts
  const [isLoadingAnyCategory, setIsLoadingAnyCategory] = useState<boolean>(false);
  const ITEMS_PER_PAGE = 25;
  const CATEGORY_PAGE_SIZE = 15; // Items per category load
  const BOOKSHELF_INITIAL_LIMIT = 50; // Initial load for bookshelf, then infinite scroll

  // Categories with "All" option prepended
  const allCategories: CategoryOption[] = [{ value: 'all', label: 'All' }, ...STORY_CATEGORIES];

  // Age range options for series (mapped to book formats)
  const ageRangeOptions: FilterOption[] = [
    { value: 'all', label: 'All Formats' },
    { value: '3-5', label: 'Picture Book' },
    { value: '5-8', label: 'Easy Reader' },
    { value: '8-12', label: 'Chapter Book' },
  ];

  // Status options for series
  const statusOptions: FilterOption[] = [
    { value: 'all', label: 'All' },
    { value: 'active', label: 'Ongoing' },
    { value: 'completed', label: 'Completed' },
  ];

  const sortOptions: FilterOption[] = [
    { value: 'trending', label: 'Trending' },
    { value: 'new', label: 'Newest' },
    { value: 'popular', label: 'Most Popular' },
  ];

  useEffect(() => {
    // Reset and reload when filters change
    if (activeTab === 'stories') {
      setStories([]);
      setHasMoreStories(true);
      // Reset category pagination for fresh load
      setCategoryPagination({});
      setCategoryOffsets({});
      loadStories(true);
    } else if (activeTab === 'series') {
      setSeries([]);
      setHasMoreSeries(true);
      loadSeries(true);
    } else if (activeTab === 'creators') {
      setCreators([]);
      setHasMoreCreators(true);
      loadCreators(true);
    }
  }, [sort, proFilter, animatedFilter, activeTab, ageRangeFilter, statusFilter]);

  // Separate effect for category changes - only triggers API call for non-bookshelf views
  // Bookshelf view handles category filtering client-side
  useEffect(() => {
    if (activeTab === 'stories' && viewMode !== 'bookshelf') {
      setStories([]);
      setHasMoreStories(true);
      loadStories(true);
    }
  }, [category]);

  // When switching view modes, reload stories appropriately
  useEffect(() => {
    if (activeTab === 'stories') {
      // Always reload when switching views - bookshelf needs all categories, list needs filtered
      setStories([]);
      setHasMoreStories(true);
      // Reset category pagination when switching to bookshelf
      if (viewMode === 'bookshelf') {
        setCategoryPagination({});
        setCategoryOffsets({});
      }
      loadStories(true);
    }
  }, [viewMode]);

  const loadStories = async (reset: boolean = false): Promise<void> => {
    // Skip if a category load is in progress (only applies to bookshelf view)
    const blockedByCategoryLoad = viewMode === 'bookshelf' && isLoadingAnyCategory;
    if (!reset && (!hasMoreStories || loadingMore || blockedByCategoryLoad)) return;

    if (reset) {
      setLoading(true);
    } else {
      setLoadingMore(true);
    }

    try {
      const offset = reset ? 0 : stories.length;
      const params = new URLSearchParams();
      // For bookshelf view, don't filter by category on backend - let it group client-side
      // For list view, apply category filter on backend
      if (category !== 'all' && viewMode !== 'bookshelf') {
        params.append('category', category);
      }
      params.append('sort', sort);
      params.append('pro_filter', proFilter);
      if (animatedFilter !== 'all') params.append('animated_filter', animatedFilter);
      params.append('limit', ITEMS_PER_PAGE.toString());
      params.append('offset', offset.toString());

      const response = await axios.get<ExploreStory[]>(`${API}/stories/explore?${params.toString()}`, {
        withCredentials: true
      });

      if (reset) {
        setStories(response.data);
      } else {
        // Deduplicate when appending to prevent duplicates from concurrent loads
        setStories(prev => {
          const existingIds = new Set(prev.map(s => s.id));
          const newStories = response.data.filter(s => !existingIds.has(s.id));
          return [...prev, ...newStories];
        });
      }

      // Check if we have more to load
      setHasMoreStories(response.data.length === ITEMS_PER_PAGE);
    } catch (error) {
      console.error('Failed to load stories:', error);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const loadSeries = async (reset: boolean = false): Promise<void> => {
    if (!reset && (!hasMoreSeries || loadingMore)) return;

    if (reset) {
      setLoading(true);
    } else {
      setLoadingMore(true);
    }

    try {
      const offset = reset ? 0 : series.length;
      const params = new URLSearchParams();
      params.append('sort', sort);
      params.append('limit', ITEMS_PER_PAGE.toString());
      params.append('offset', offset.toString());
      if (ageRangeFilter !== 'all') params.append('age_range', ageRangeFilter);
      if (statusFilter !== 'all') params.append('status', statusFilter);

      const response = await axios.get<SeriesResponse>(`${API}/v1/series/public/explore?${params.toString()}`, {
        withCredentials: true
      });

      const newSeries = response.data.series || [];
      if (reset) {
        setSeries(newSeries);
      } else {
        setSeries(prev => [...prev, ...newSeries]);
      }

      // Check if we have more to load
      setHasMoreSeries(newSeries.length === ITEMS_PER_PAGE);
    } catch (error) {
      console.error('Failed to load series:', error);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const loadCreators = async (reset: boolean = false): Promise<void> => {
    if (!reset && (!hasMoreCreators || loadingMore)) return;

    if (reset) {
      setLoading(true);
    } else {
      setLoadingMore(true);
    }

    try {
      const offset = reset ? 0 : creators.length;
      const response = await axios.get<ExploreCreator[]>(
        `${API}/creators/featured?limit=${ITEMS_PER_PAGE}&offset=${offset}`,
        { withCredentials: true }
      );

      if (reset) {
        setCreators(response.data);
      } else {
        setCreators(prev => [...prev, ...response.data]);
      }

      // Check if we have more to load
      setHasMoreCreators(response.data.length === ITEMS_PER_PAGE);
    } catch (error) {
      console.error('Failed to load creators:', error);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const handleFollowCreator = async (creatorId: string, e: React.MouseEvent): Promise<void> => {
    e.stopPropagation();

    // Optimistic update
    setCreators(prev => prev.map(c => {
      if (c.user_id === creatorId) {
        return {
          ...c,
          is_following: !c.is_following,
          followers_count: c.is_following ? c.followers_count - 1 : c.followers_count + 1
        };
      }
      return c;
    }));

    try {
      await axios.post(`${API}/profile/${creatorId}/follow`, {}, { withCredentials: true });
    } catch (error) {
      // Revert on error
      setCreators(prev => prev.map(c => {
        if (c.user_id === creatorId) {
          return {
            ...c,
            is_following: !c.is_following,
            followers_count: c.is_following ? c.followers_count - 1 : c.followers_count + 1
          };
        }
        return c;
      }));
      console.error('Failed to follow/unfollow:', error);
    }
  };

  // Load more stories for a specific category (horizontal pagination)
  const loadMoreCategory = useCallback(async (categoryToLoad: string): Promise<void> => {
    // Don't load if already loading this category or any other category
    if (categoryPagination[categoryToLoad]?.loading || isLoadingAnyCategory) return;

    // Set loading state for this category and global flag
    setIsLoadingAnyCategory(true);
    setCategoryPagination(prev => ({
      ...prev,
      [categoryToLoad]: { ...prev[categoryToLoad], loading: true }
    }));

    try {
      const offset = categoryOffsets[categoryToLoad] || 0;
      const params = new URLSearchParams();
      params.append('category', categoryToLoad);
      params.append('sort', sort);
      params.append('pro_filter', proFilter);
      if (animatedFilter !== 'all') params.append('animated_filter', animatedFilter);
      params.append('limit', CATEGORY_PAGE_SIZE.toString());
      params.append('offset', offset.toString());

      const response = await axios.get<ExploreStory[]>(`${API}/stories/explore?${params.toString()}`, {
        withCredentials: true
      });

      // Append new stories to existing list, deduplicating by ID
      if (response.data.length > 0) {
        setStories(prev => {
          const existingIds = new Set(prev.map(s => s.id));
          const newStories = response.data.filter(s => !existingIds.has(s.id));
          return [...prev, ...newStories];
        });
        setCategoryOffsets(prev => ({
          ...prev,
          [categoryToLoad]: offset + response.data.length
        }));
      }

      // Update hasMore for this category
      const hasMore = response.data.length === CATEGORY_PAGE_SIZE;
      setCategoryPagination(prev => ({
        ...prev,
        [categoryToLoad]: { hasMore, loading: false }
      }));
    } catch (error) {
      console.error(`Failed to load more ${categoryToLoad} stories:`, error);
      setCategoryPagination(prev => ({
        ...prev,
        [categoryToLoad]: { ...prev[categoryToLoad], loading: false }
      }));
    } finally {
      setIsLoadingAnyCategory(false);
    }
  }, [categoryPagination, categoryOffsets, sort, proFilter, animatedFilter, isLoadingAnyCategory]);

  // Infinite scroll handler
  const handleLoadMore = useCallback(() => {
    // Skip vertical loading if a horizontal category load is in progress (bookshelf only)
    if (viewMode === 'bookshelf' && isLoadingAnyCategory) return;

    if (activeTab === 'stories') {
      loadStories(false);
    } else if (activeTab === 'series') {
      loadSeries(false);
    } else if (activeTab === 'creators') {
      loadCreators(false);
    }
  }, [activeTab, viewMode, stories.length, series.length, creators.length, hasMoreStories, hasMoreSeries, hasMoreCreators, isLoadingAnyCategory]);

  // Hook up infinite scroll - returns a ref to attach to loader element
  const hasMore = activeTab === 'stories' ? hasMoreStories : activeTab === 'series' ? hasMoreSeries : hasMoreCreators;
  const loaderRef = useInfiniteScroll({
    onLoadMore: handleLoadMore,
    hasMore,
    loading: loading || loadingMore
  });

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  // Convert ExploreStory to BookshelfStory format for the bookshelf view
  // No slice - supports infinite scroll for thousands of stories
  const bookshelfStories: BookshelfStory[] = useMemo(() => {
    return stories.map((story) => ({
      id: story.id,
      title: story.title,
      thumbnail: story.thumbnail ? getImageUrl(story.thumbnail) : null,
      age_range: story.age_range as AgeRange,
      category: story.category, // Use story's actual category for proper grouping
      author_name: story.author_name,
      author_picture: story.author_picture,
      user_id: story.user_id,
      created_at: story.created_at,
      favorites_count: story.favorites_count,
      views_count: story.views_count,
      is_favorited: story.is_favorited,
      version: story.version,
      is_pro_story: story.is_pro_story,
      has_animations: story.has_animations,
    }));
  }, [stories]);

  // Compute the display count based on view mode and category filter
  const displayStoryCount = useMemo(() => {
    if (viewMode === 'bookshelf' && category !== 'all') {
      // In bookshelf view with category filter, count only stories in that category
      return bookshelfStories.filter(s =>
        s.category?.toLowerCase().trim() === category.toLowerCase()
      ).length;
    }
    return stories.length;
  }, [viewMode, category, stories.length, bookshelfStories]);

  // Initialize category pagination based on current story counts
  // Categories with stories likely have more to load
  useEffect(() => {
    if (viewMode !== 'bookshelf' || stories.length === 0) return;

    // Count stories per category
    const categoryCounts: Record<string, number> = {};
    stories.forEach(story => {
      const cat = story.category?.toLowerCase().trim() || 'uncategorized';
      categoryCounts[cat] = (categoryCounts[cat] || 0) + 1;
    });

    // Initialize pagination state - assume hasMore if we have any stories in category
    // This will be updated when actual load returns fewer than page size
    const newPagination: CategoryPaginationState = {};
    Object.entries(categoryCounts).forEach(([cat, count]) => {
      // If we don't have pagination info yet, assume there could be more
      if (!categoryPagination[cat]) {
        newPagination[cat] = { hasMore: true, loading: false };
      }
    });

    if (Object.keys(newPagination).length > 0) {
      setCategoryPagination(prev => ({ ...prev, ...newPagination }));
    }

    // Initialize offsets based on current counts
    const newOffsets: Record<string, number> = {};
    Object.entries(categoryCounts).forEach(([cat, count]) => {
      if (!categoryOffsets[cat]) {
        newOffsets[cat] = count;
      }
    });

    if (Object.keys(newOffsets).length > 0) {
      setCategoryOffsets(prev => ({ ...prev, ...newOffsets }));
    }
  }, [stories, viewMode]);

  // Handle view story navigation
  const handleViewStory = useCallback((storyId: string) => {
    navigate(`/story/${storyId}`);
  }, [navigate]);

  // Story List Item Component (matching Library/Search page style)
  const StoryListItem = ({ story }: StoryListItemProps): ReactElement => (
    <div
      className="flex items-center gap-4 p-3 hover:bg-black/5 dark:hover:bg-white/10 hover:scale-[1.01] transition-all duration-200 cursor-pointer group"
      onClick={() => navigate(`/story/${story.id}`)}
      style={{ borderBottom: '1px solid var(--st-border-light)' }}
    >
      {/* Thumbnail with page count overlay */}
      <div className="relative w-16 h-16 md:w-20 md:h-20 rounded-xl overflow-hidden flex-shrink-0">
        {story.thumbnail ? (
          <img src={getImageUrl(story.thumbnail) || undefined} alt={story.title} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center" style={{ background: 'linear-gradient(135deg, var(--st-border-light) 0%, var(--st-border-medium) 100%)' }}>
            <Sparkles className="w-6 h-6" style={{ color: 'var(--st-primary-hover)' }} />
          </div>
        )}
        {story.pages && story.pages.length > 0 && (
          <div className="absolute bottom-1 right-1 px-1.5 py-0.5 rounded text-xs font-bold text-white" style={{ background: 'rgba(0,0,0,0.6)', fontSize: '10px' }}>
            {story.pages?.length || 0}p
          </div>
        )}
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1 flex-wrap">
          <h3 className="font-bold text-sm md:text-base truncate" style={{ fontFamily: 'Chewy, cursive', color: 'var(--st-text-heading)' }}>
            {story.title}
          </h3>
          <span className="px-2 py-0.5 rounded-full text-xs font-semibold flex-shrink-0 bg-gray-200 dark:bg-gray-700" style={{ color: 'var(--st-text-heading)' }}>
            {getBookTypeLabel(story.age_range)}
          </span>
          <VersionBadge version={story.version} isProStory={story.is_pro_story} size="small" />
          {story.series_title && story.series_is_public && (
            <span title={`Part of: ${story.series_title}`}>
              <Library className="w-3.5 h-3.5 flex-shrink-0" style={{ color: '#4F46E5' }} />
            </span>
          )}
          {story.has_animations && (
            <span title="Animated Pages">
              <Film className="w-3.5 h-3.5 flex-shrink-0" style={{ color: '#F59E0B' }} />
            </span>
          )}
          {story.is_public === false && (
            <Lock className="w-3.5 h-3.5 flex-shrink-0" style={{ color: 'var(--st-text-muted)' }} />
          )}
        </div>
        <div className="flex items-center gap-2 mt-1">
          <ClickableAuthor
            userId={story.user_id}
            name={story.author_name}
            picture={story.author_picture}
            size="small"
          />
          <span className="text-xs hidden md:inline" style={{ color: 'var(--st-text-muted)' }}>•</span>
          <span className="text-xs hidden md:inline" style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-muted)' }}>{formatDate(story.created_at)}</span>
        </div>
      </div>

      {/* Stats */}
      <div className="hidden md:flex items-center gap-4 text-xs flex-shrink-0" style={{ color: 'var(--st-text-muted)' }}>
        {story.views_count && story.views_count > 0 && (
          <span className="flex items-center gap-1">
            <Eye className="w-4 h-4" />
            {story.views_count}
          </span>
        )}
        <span className="flex items-center gap-1">
          <Heart className="w-4 h-4" fill={story.is_favorited ? 'var(--st-primary)' : 'none'} stroke={story.is_favorited ? 'var(--st-primary)' : 'currentColor'} />
          {story.favorites_count || 0}
        </span>
      </div>
    </div>
  );

  return (
    <PageLayout>
      <div className="flex-1 overflow-y-auto overflow-x-hidden">
        <div className="max-w-screen-2xl mx-auto px-4 py-4 md:py-6 md:px-8 2xl:px-12 overflow-x-hidden">
          {/* Header */}
          <div className="rounded-2xl p-4 md:p-5 mb-5 shadow-sm" style={{ background: 'var(--st-bg-surface)', border: '1px solid var(--st-border-light)' }}>
            <div className="flex items-center justify-between mb-4">
              <div>
                <h1 className="text-xl md:text-2xl font-bold" style={{ fontFamily: 'Chewy, cursive', color: 'var(--st-text-heading)' }}>
                  Explore
                </h1>
                <p className="text-sm" style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-muted)' }}>
                  Discover content from our community
                </p>
              </div>
              <button
                onClick={() => navigate('/create')}
                className="hidden sm:flex items-center gap-2 px-4 py-2.5 rounded-lg font-semibold text-sm"
                style={{
                  background: 'linear-gradient(135deg, var(--st-primary-hover) 0%, var(--st-primary) 100%)',
                  color: 'white',
                  fontFamily: 'Quicksand, sans-serif'
                }}
              >
                <Sparkles className="w-4 h-4" />
                Create Story
              </button>
            </div>

            {/* Tab Switcher and View Toggle Row */}
            <div className="flex items-center justify-between gap-2 md:gap-4">
              {/* Tab Switcher */}
              <div className="flex items-center gap-1 p-1 rounded-xl bg-[#F5F0EB] dark:bg-white/10">
                <button
                  onClick={() => setActiveTab('stories')}
                  className={`flex items-center gap-1.5 px-2.5 py-1.5 md:px-4 md:py-2 rounded-lg text-xs md:text-sm font-semibold transition-all duration-200 ${
                    activeTab === 'stories' ? 'shadow-sm' : 'hover:bg-white/50 dark:hover:bg-white/10'
                  }`}
                  style={{
                    ...(activeTab === 'stories' ? { background: 'var(--st-bg-surface)' } : {}),
                    fontFamily: 'Quicksand, sans-serif',
                    color: activeTab === 'stories' ? 'var(--st-text-heading)' : 'var(--st-text-muted)'
                  }}
                >
                  <BookOpen className="w-4 h-4" style={{ color: activeTab === 'stories' ? 'var(--st-primary)' : 'var(--st-text-muted)' }} />
                  <span className="hidden sm:inline">Stories</span>
                </button>
                <button
                  onClick={() => setActiveTab('series')}
                  className={`flex items-center gap-1.5 px-2.5 py-1.5 md:px-4 md:py-2 rounded-lg text-xs md:text-sm font-semibold transition-all duration-200 ${
                    activeTab === 'series' ? 'shadow-sm' : 'hover:bg-white/50 dark:hover:bg-white/10'
                  }`}
                  style={{
                    ...(activeTab === 'series' ? { background: 'var(--st-bg-surface)' } : {}),
                    fontFamily: 'Quicksand, sans-serif',
                    color: activeTab === 'series' ? 'var(--st-text-heading)' : 'var(--st-text-muted)'
                  }}
                >
                  <Library className="w-4 h-4" style={{ color: activeTab === 'series' ? '#4F46E5' : 'var(--st-text-muted)' }} />
                  <span className="hidden sm:inline">Series</span>
                </button>
                <button
                  onClick={() => setActiveTab('creators')}
                  className={`flex items-center gap-1.5 px-2.5 py-1.5 md:px-4 md:py-2 rounded-lg text-xs md:text-sm font-semibold transition-all duration-200 ${
                    activeTab === 'creators' ? 'shadow-sm' : 'hover:bg-white/50 dark:hover:bg-white/10'
                  }`}
                  style={{
                    ...(activeTab === 'creators' ? { background: 'var(--st-bg-surface)' } : {}),
                    fontFamily: 'Quicksand, sans-serif',
                    color: activeTab === 'creators' ? 'var(--st-text-heading)' : 'var(--st-text-muted)'
                  }}
                >
                  <Users className="w-4 h-4" style={{ color: activeTab === 'creators' ? '#10B981' : 'var(--st-text-muted)' }} />
                  <span className="hidden sm:inline">Creators</span>
                </button>
              </div>

              {/* View Mode Toggle - only for stories tab */}
              {activeTab === 'stories' && (
                <div className="flex items-center gap-1 p-1 rounded-xl bg-[#F5F0EB] dark:bg-white/10">
                  <button
                    onClick={() => setViewMode('bookshelf')}
                    className={`flex items-center gap-1.5 px-2.5 py-1.5 md:px-3 rounded-lg text-xs font-semibold transition-all duration-200 ${
                      viewMode === 'bookshelf' ? 'shadow-sm' : 'hover:bg-white/50 dark:hover:bg-white/10'
                    }`}
                    style={{
                      ...(viewMode === 'bookshelf' ? { background: 'var(--st-bg-surface)' } : {}),
                      fontFamily: 'Quicksand, sans-serif',
                      color: viewMode === 'bookshelf' ? 'var(--st-text-heading)' : 'var(--st-text-muted)'
                    }}
                    title="Magic Bookshelf"
                  >
                    <BookMarked className="w-4 h-4" style={{ color: viewMode === 'bookshelf' ? '#B8936A' : 'var(--st-text-muted)' }} />
                    <span className="hidden sm:inline">Bookshelf</span>
                  </button>
                  <button
                    onClick={() => setViewMode('list')}
                    className={`flex items-center gap-1.5 px-2.5 py-1.5 md:px-3 rounded-lg text-xs font-semibold transition-all duration-200 ${
                      viewMode === 'list' ? 'shadow-sm' : 'hover:bg-white/50 dark:hover:bg-white/10'
                    }`}
                    style={{
                      ...(viewMode === 'list' ? { background: 'var(--st-bg-surface)' } : {}),
                      fontFamily: 'Quicksand, sans-serif',
                      color: viewMode === 'list' ? 'var(--st-text-heading)' : 'var(--st-text-muted)'
                    }}
                    title="List view"
                  >
                    <List className="w-4 h-4" style={{ color: viewMode === 'list' ? 'var(--st-primary)' : 'var(--st-text-muted)' }} />
                    <span className="hidden sm:inline">List</span>
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Categories - only show for stories tab */}
          {activeTab === 'stories' && (
            <div className="flex flex-wrap gap-1 p-1 mb-5 rounded-xl bg-[#F5F0EB] dark:bg-white/10">
              {allCategories.map((cat) => {
                const isSelected = category === cat.value;
                const Icon = cat.icon || Sparkles;
                return (
                  <button
                    key={cat.value}
                    data-testid={`category-${cat.value}`}
                    onClick={() => setCategory(cat.value)}
                    className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold transition-all duration-200 ${
                      isSelected ? 'shadow-sm' : 'hover:bg-white/50 dark:hover:bg-white/10'
                    }`}
                    style={{
                      ...(isSelected ? { background: 'var(--st-bg-surface)' } : {}),
                      fontFamily: 'Quicksand, sans-serif',
                      color: isSelected ? 'var(--st-text-heading)' : 'var(--st-text-muted)'
                    }}
                  >
                    <Icon className="w-3.5 h-3.5" style={{ color: isSelected ? 'var(--st-primary)' : 'var(--st-text-muted)' }} />
                    {cat.label}
                  </button>
                );
              })}
            </div>
          )}

          {/* Series Filters - only show for series tab */}
          {activeTab === 'series' && (
            <div className="flex flex-wrap items-center gap-3 mb-5">
              {/* Age Range Filter */}
              <div className="flex items-center gap-1 p-1 rounded-xl bg-indigo-100 dark:bg-indigo-900/30">
                {ageRangeOptions.map((option) => (
                  <button
                    key={option.value}
                    onClick={() => setAgeRangeFilter(option.value)}
                    className={`px-3 py-2 rounded-lg text-xs font-semibold transition-all duration-200 ${
                      ageRangeFilter === option.value
                        ? 'shadow-sm text-indigo-600 dark:text-indigo-300'
                        : 'hover:bg-white/50 dark:hover:bg-white/10 text-indigo-500 dark:text-indigo-400'
                    }`}
                    style={{
                      ...(ageRangeFilter === option.value ? { background: 'var(--st-bg-surface)' } : {}),
                      fontFamily: 'Quicksand, sans-serif'
                    }}
                  >
                    {option.label}
                  </button>
                ))}
              </div>

              {/* Status Filter */}
              <div className="flex items-center gap-1 p-1 rounded-xl bg-indigo-100 dark:bg-indigo-900/30">
                {statusOptions.map((option) => (
                  <button
                    key={option.value}
                    onClick={() => setStatusFilter(option.value)}
                    className={`px-3 py-2 rounded-lg text-xs font-semibold transition-all duration-200 ${
                      statusFilter === option.value
                        ? 'shadow-sm text-indigo-600 dark:text-indigo-300'
                        : 'hover:bg-white/50 dark:hover:bg-white/10 text-indigo-500 dark:text-indigo-400'
                    }`}
                    style={{
                      ...(statusFilter === option.value ? { background: 'var(--st-bg-surface)' } : {}),
                      fontFamily: 'Quicksand, sans-serif'
                    }}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Sort Row */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 gap-3">
            <div className="flex items-center gap-3 flex-shrink-0">
              <h2 className="text-base font-bold" style={{ fontFamily: 'Chewy, cursive', color: 'var(--st-text-heading)' }}>
                {activeTab === 'stories'
                  ? (category === 'all' ? 'All Stories' : allCategories.find(c => c.value === category)?.label || 'Stories')
                  : 'All Series'
                }
              </h2>
              <span className="text-sm" style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-muted)' }}>
                {activeTab === 'stories'
                  ? `${displayStoryCount} ${displayStoryCount === 1 ? 'story' : 'stories'}`
                  : `${series.length} ${series.length === 1 ? 'series' : 'series'}`
                }
              </span>
            </div>

            <div className="flex items-center gap-2 overflow-x-auto pb-1 -mb-1 scrollbar-hide" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
              {/* Pro Filter - only show for stories */}
              {activeTab === 'stories' && (
                <div className="flex items-center gap-1 p-1 rounded-xl bg-[#F5F0EB] dark:bg-white/10 flex-shrink-0">
                  {([
                    { value: 'all', label: 'All' },
                    { value: 'pro', label: 'Pro' },
                    { value: 'standard', label: 'Standard' }
                  ] as FilterOption[]).map((filter) => (
                    <button
                      key={filter.value}
                      onClick={() => setProFilter(filter.value as ProFilterType)}
                      className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all duration-200 flex items-center gap-1 ${
                        proFilter === filter.value ? 'shadow-sm' : 'hover:bg-white/50 dark:hover:bg-white/10'
                      }`}
                      style={{
                        ...(proFilter === filter.value ? { background: 'var(--st-bg-surface)' } : {}),
                        fontFamily: 'Quicksand, sans-serif',
                        color: proFilter === filter.value
                          ? filter.value === 'pro' ? '#9B59B6' : 'var(--st-text-heading)'
                          : 'var(--st-text-muted)'
                      }}
                    >
                      {filter.value === 'pro' && <Star className="w-3 h-3" style={{ color: proFilter === 'pro' ? '#9B59B6' : 'var(--st-text-muted)' }} />}
                      {filter.label}
                    </button>
                  ))}
                </div>
              )}

              {/* Animated Filter - only show for stories */}
              {activeTab === 'stories' && (
                <div className="flex items-center gap-1 p-1 rounded-xl bg-[#F5F0EB] dark:bg-white/10 flex-shrink-0">
                  {([
                    { value: 'all', label: 'All' },
                    { value: 'animated', label: 'Animated' },
                    { value: 'static', label: 'Static' }
                  ] as FilterOption[]).map((filter) => (
                    <button
                      key={filter.value}
                      onClick={() => setAnimatedFilter(filter.value as AnimatedFilterType)}
                      className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all duration-200 flex items-center gap-1 ${
                        animatedFilter === filter.value ? 'shadow-sm' : 'hover:bg-white/50 dark:hover:bg-white/10'
                      }`}
                      style={{
                        ...(animatedFilter === filter.value ? { background: 'var(--st-bg-surface)' } : {}),
                        fontFamily: 'Quicksand, sans-serif',
                        color: animatedFilter === filter.value
                          ? filter.value === 'animated' ? '#E74C3C' : 'var(--st-text-heading)'
                          : 'var(--st-text-muted)'
                      }}
                    >
                      {filter.value === 'animated' && <Film className="w-3 h-3" style={{ color: animatedFilter === 'animated' ? '#E74C3C' : 'var(--st-text-muted)' }} />}
                      {filter.label}
                    </button>
                  ))}
                </div>
              )}

              {/* Sort Dropdown */}
              <div className="relative flex-shrink-0">
                <button
                  data-testid="sort-select"
                  onClick={() => setSortDropdownOpen(!sortDropdownOpen)}
                  className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-semibold cursor-pointer transition-all duration-200 hover:bg-white/50 dark:hover:bg-white/10 bg-[#F5F0EB] dark:bg-white/10"
                  style={{
                    fontFamily: 'Quicksand, sans-serif',
                    color: 'var(--st-text-heading)'
                  }}
                >
                  <span>{sortOptions.find(o => o.value === sort)?.label}</span>
                  <ChevronDown className={`w-3.5 h-3.5 transition-transform ${sortDropdownOpen ? 'rotate-180' : ''}`} style={{ color: 'var(--st-text-muted)' }} />
                </button>

                {sortDropdownOpen && (
                  <>
                    <div className="fixed inset-0 z-10" onClick={() => setSortDropdownOpen(false)} />
                    <div
                      className="absolute right-0 top-full mt-2 z-20 rounded-xl shadow-lg overflow-hidden"
                      style={{ background: 'var(--st-bg-surface)', border: '1px solid var(--st-border-light)', minWidth: '140px' }}
                    >
                      {sortOptions.map((option) => (
                        <button
                          key={option.value}
                          onClick={() => {
                            setSort(option.value as SortType);
                            setSortDropdownOpen(false);
                          }}
                          className="w-full px-4 py-2.5 text-left text-xs font-semibold transition-colors hover:bg-black/5 dark:hover:bg-white/10 flex items-center gap-2"
                          style={{
                            fontFamily: 'Quicksand, sans-serif',
                            color: sort === option.value ? 'var(--st-primary)' : 'var(--st-text-heading)',
                            background: 'var(--st-bg-surface)'
                          }}
                        >
                          {sort === option.value && <span style={{ color: 'var(--st-primary)' }}>✓</span>}
                          <span className={sort === option.value ? '' : 'ml-5'}>{option.label}</span>
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Content */}
          {loading ? (
            <div className="text-center py-16 rounded-2xl shadow-sm" style={{ background: 'var(--st-bg-surface)', border: '1px solid var(--st-border-light)' }}>
              {activeTab === 'stories' ? (
                <BookOpen className="w-12 h-12 mx-auto mb-4 animate-pulse" style={{ color: 'var(--st-primary)' }} />
              ) : (
                <Library className="w-12 h-12 mx-auto mb-4 animate-pulse" style={{ color: '#4F46E5' }} />
              )}
              <p style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-muted)' }}>
                Loading {activeTab === 'stories' ? 'stories' : 'series'}...
              </p>
            </div>
          ) : activeTab === 'stories' ? (
            /* Stories Tab Content */
            stories.length === 0 ? (
              <div className="text-center py-16 rounded-2xl shadow-sm" style={{ background: 'var(--st-bg-surface)', border: '1px solid var(--st-border-light)' }}>
                <BookOpen className="w-12 h-12 mx-auto mb-4" style={{ color: 'var(--st-border-medium)' }} />
                <p className="text-lg font-bold mb-2" style={{ fontFamily: 'Chewy, cursive', color: 'var(--st-text-heading)' }}>
                  No stories found
                </p>
                <p className="text-sm mb-4" style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-muted)' }}>
                  Try a different category or be the first to create!
                </p>
                <button
                  onClick={() => navigate('/create')}
                  className="px-5 py-2.5 rounded-lg font-semibold text-sm transition-all"
                  style={{
                    background: 'linear-gradient(135deg, var(--st-primary-hover) 0%, var(--st-primary) 100%)',
                    color: 'white',
                    fontFamily: 'Quicksand, sans-serif'
                  }}
                >
                  Create Story
                </button>
              </div>
            ) : viewMode === 'bookshelf' ? (
              /* Bookshelf View */
              <div
                className="rounded-2xl overflow-hidden shadow-sm"
                style={{
                  background: 'var(--st-bg-surface)',
                  border: '1px solid var(--st-border-light)',
                  height: 'calc(100vh - 350px)',
                  minHeight: '500px',
                }}
              >
                <Suspense
                  fallback={
                    <div
                      className="w-full h-full flex items-center justify-center"
                      style={{ background: 'linear-gradient(180deg, #FFF8F0 0%, #FFE8D6 100%)' }}
                    >
                      <Loader2 className="w-10 h-10 animate-spin" style={{ color: '#B8936A' }} />
                    </div>
                  }
                >
                  <MagicBookshelf
                    stories={bookshelfStories}
                    onViewStory={handleViewStory}
                    loading={loading}
                    loadingMore={loadingMore}
                    hasMore={hasMoreStories}
                    onLoadMore={handleLoadMore}
                    activeCategory={category}
                    categoryPagination={categoryPagination}
                    onLoadMoreCategory={loadMoreCategory}
                  />
                </Suspense>
              </div>
            ) : (
              /* List View */
              <>
                <div className="rounded-2xl overflow-hidden shadow-sm" style={{ background: 'var(--st-bg-surface)', border: '1px solid var(--st-border-light)' }}>
                  {stories.map((story) => (
                    <StoryListItem key={story.id} story={story} />
                  ))}
                </div>

                {/* Infinite scroll trigger element */}
                {hasMoreStories && (
                  <div ref={loaderRef} className="flex justify-center py-6">
                    {loadingMore && (
                      <>
                        <Loader2 className="w-6 h-6 animate-spin" style={{ color: 'var(--st-primary)' }} />
                        <p className="text-sm ml-2" style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-muted)' }}>
                          Loading more stories...
                        </p>
                      </>
                    )}
                  </div>
                )}

                {/* End of Results */}
                {!hasMoreStories && stories.length > 0 && (
                  <div className="text-center py-6">
                    <p className="text-sm" style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-muted)' }}>
                      You've reached the end!
                    </p>
                  </div>
                )}
              </>
            )
          ) : activeTab === 'series' ? (
            /* Series Tab Content */
            series.length === 0 ? (
              <div className="text-center py-16 rounded-2xl shadow-sm" style={{ background: 'var(--st-bg-surface)', border: '1px solid var(--st-border-light)' }}>
                <Library className="w-12 h-12 mx-auto mb-4" style={{ color: '#C7D2FE' }} />
                <p className="text-lg font-bold mb-2" style={{ fontFamily: 'Chewy, cursive', color: 'var(--st-text-heading)' }}>
                  No public series yet
                </p>
                <p className="text-sm mb-4" style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-muted)' }}>
                  Be the first to share your series with the community!
                </p>
                <button
                  onClick={() => navigate('/series')}
                  className="px-5 py-2.5 rounded-lg font-semibold text-sm transition-all"
                  style={{
                    background: 'linear-gradient(135deg, #818CF8 0%, #4F46E5 100%)',
                    color: 'white',
                    fontFamily: 'Quicksand, sans-serif'
                  }}
                >
                  Create a Series
                </button>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                  {series.map((s) => (
                    <PublicSeriesCard key={s.id} series={s} onClick={undefined} onFavoriteChange={undefined} />
                  ))}
                </div>

                {/* Infinite scroll trigger element */}
                {hasMoreSeries && (
                  <div ref={loaderRef} className="flex justify-center py-6">
                    {loadingMore && (
                      <>
                        <Loader2 className="w-6 h-6 animate-spin" style={{ color: '#4F46E5' }} />
                        <p className="text-sm ml-2" style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-muted)' }}>
                          Loading more series...
                        </p>
                      </>
                    )}
                  </div>
                )}

                {/* End of Results */}
                {!hasMoreSeries && series.length > 0 && (
                  <div className="text-center py-6">
                    <p className="text-sm" style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-muted)' }}>
                      You've reached the end!
                    </p>
                  </div>
                )}
              </>
            )
          ) : (
            /* Creators Tab Content */
            creators.length === 0 ? (
              <div className="text-center py-16 rounded-2xl shadow-sm" style={{ background: 'var(--st-bg-surface)', border: '1px solid var(--st-border-light)' }}>
                <Users className="w-12 h-12 mx-auto mb-4" style={{ color: 'var(--st-border-medium)' }} />
                <p className="text-lg font-bold mb-2" style={{ fontFamily: 'Chewy, cursive', color: 'var(--st-text-heading)' }}>
                  No creators found
                </p>
                <p className="text-sm" style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-muted)' }}>
                  Be the first to share your stories!
                </p>
              </div>
            ) : (
              <>
              <div className="rounded-xl overflow-hidden" style={{ background: 'var(--st-bg-surface)', border: '1px solid var(--st-border-light)' }}>
                {creators.map((creator, index) => (
                  <div
                    key={creator.user_id}
                    className="flex items-center gap-4 p-4 cursor-pointer transition-all hover:bg-black/5 dark:hover:bg-white/5"
                    style={{ borderBottom: index < creators.length - 1 ? '1px solid var(--st-border-light)' : 'none' }}
                    onClick={() => navigate(`/user/${creator.user_id}`)}
                  >
                    {/* Avatar */}
                    <div
                      className="w-12 h-12 rounded-full flex-shrink-0 overflow-hidden"
                      style={{ background: 'var(--st-bg-muted)' }}
                    >
                      {creator.picture ? (
                        <img
                          src={getImageUrl(creator.picture) || undefined}
                          alt={creator.name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <UserIcon className="w-6 h-6" style={{ color: 'var(--st-text-muted)' }} />
                        </div>
                      )}
                    </div>

                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h3
                          className="font-bold text-sm truncate"
                          style={{ fontFamily: 'Chewy, cursive', color: 'var(--st-text-heading)' }}
                        >
                          {creator.name}
                        </h3>
                        {creator.is_premium && (
                          <Crown className="w-3.5 h-3.5 flex-shrink-0" style={{ color: '#9B59B6' }} />
                        )}
                        {creator.handle && (
                          <span
                            className="text-xs truncate"
                            style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-muted)' }}
                          >
                            @{creator.handle}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2 text-xs mt-0.5" style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-muted)' }}>
                        <span>{creator.story_count} {creator.story_count === 1 ? 'story' : 'stories'}</span>
                        <span>•</span>
                        <span>{creator.followers_count} {creator.followers_count === 1 ? 'follower' : 'followers'}</span>
                      </div>
                    </div>

                    {/* Follow Button */}
                    <button
                      onClick={(e) => handleFollowCreator(creator.user_id, e)}
                      className="px-4 py-1.5 rounded-lg text-xs font-semibold transition-all flex-shrink-0"
                      style={{
                        fontFamily: 'Quicksand, sans-serif',
                        background: creator.is_following ? 'var(--st-bg-muted)' : 'var(--st-primary)',
                        color: creator.is_following ? 'var(--st-text-heading)' : 'white',
                      }}
                    >
                      {creator.is_following ? 'Following' : 'Follow'}
                    </button>
                  </div>
                ))}
              </div>

              {/* Infinite scroll trigger element */}
              {hasMoreCreators && (
                <div ref={loaderRef} className="flex justify-center py-6">
                  {loadingMore && (
                    <>
                      <Loader2 className="w-6 h-6 animate-spin" style={{ color: 'var(--st-primary)' }} />
                      <p className="text-sm ml-2" style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-muted)' }}>
                        Loading more creators...
                      </p>
                    </>
                  )}
                </div>
              )}

              {/* End of Results */}
              {!hasMoreCreators && creators.length > 0 && (
                <div className="text-center py-6">
                  <p className="text-sm" style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-muted)' }}>
                    You've reached the end!
                  </p>
                </div>
              )}
            </>
            )
          )}
        </div>
      </div>
    </PageLayout>
  );
};

export default ExplorePage;
